import Cookies from 'js-cookie';
import dayjs from '../dayjs';

import { deleteClientData, getClientData, setClientData } from '../clientData';
import { hasConsent, onConsentChange } from './consentState';

export const ATTRIBUTION_KEY = 'vansite.attribution';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
const MAX_PARAM_LENGTH = 200;
const ATTRIBUTION_TTL_MONTHS = 6;

// Held in memory until marketing consent allows persisting.
let pendingTouch = null;

const buildFbc = (fbclid) => `fb.1.${Date.now()}.${fbclid}`;

/** Sets _fbc ourselves when the pixel could not (ad blocker). */
export const ensureFbcCookie = (touch) => {
  if (typeof document === 'undefined') return;
  if (!touch || !touch.fbclid || Cookies.get('_fbc')) return;
  Cookies.set('_fbc', buildFbc(touch.fbclid), { expires: 90, sameSite: 'Lax' });
};

const persist = (touch) => {
  const stored = getClientData(ATTRIBUTION_KEY) || {};
  const next = { firstTouch: stored.firstTouch || touch, lastTouch: touch };
  setClientData(ATTRIBUTION_KEY, next, dayjs().add(ATTRIBUTION_TTL_MONTHS, 'months'), {
    sameSite: 'Lax',
  });
  ensureFbcCookie(touch);
  // Once durably stored, the in-memory fallback is redundant — clear it so a
  // later consent-change event can't re-persist a stale touch.
  pendingTouch = null;
};

/**
 * Reads utm_*, gclid and fbclid from the landing URL. Kept in memory
 * immediately; persisted only with marketing consent (GDPR).
 * @param {string} [search] defaults to window.location.search
 * @returns {object|null} the captured touch or null
 */
export const captureAttributionFromUrl = (search) => {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(search != null ? search : window.location.search);
  const touch = {};
  [...UTM_KEYS, 'gclid', 'fbclid'].forEach((key) => {
    const value = params.get(key);
    if (value) touch[key] = value.slice(0, MAX_PARAM_LENGTH);
  });
  if (Object.keys(touch).length === 0) return null;
  touch.ts = new Date().toISOString();
  pendingTouch = touch;
  if (hasConsent('marketing')) persist(touch);
  return touch;
};

/**
 * @returns {{firstTouch: object, lastTouch: object} | null}
 */
export const getAttribution = () => {
  const stored = typeof window !== 'undefined' ? getClientData(ATTRIBUTION_KEY) : null;
  if (stored) return stored;
  return pendingTouch ? { firstTouch: pendingTouch, lastTouch: pendingTouch } : null;
};

// Late grant: persist the landing touch captured before the banner decision.
// Withdrawal (GDPR Art. 7(3)): drop the stored touch and the in-memory fallback
// so a later re-grant can't re-transmit a pre-withdrawal touch.
onConsentChange((consent) => {
  if (consent.marketing) {
    if (pendingTouch) persist(pendingTouch);
  } else {
    pendingTouch = null;
    deleteClientData(ATTRIBUTION_KEY);
  }
});
